import React, { useState, useEffect } from 'react';
import {
    Table,
    Select,
    DatePicker,
    Button,
    Card,
    Typography,
    Tooltip,
    message,
    Tabs,
    Slider,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useAuthOrg } from '../../context/AuthOrgContext';
import { useMetaSetup } from '../../context/MetaSetupContext';
import coreAPIService from '../../apiServices/coreAPIService';
import { CTW_API_ENDPOINT, CTW_NODE_API_ENDPOINT } from '../../utils';
import CommonLayout from '../../common/LayoutBlock';
import Heading from '../../common/Heading';
import { useBreadcrumbs } from '../../context/BreadcrumbContext';
import { useNavigate } from 'react-router-dom';
import { normalizeRole, USER_ROLES } from '../../utils/roles';

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

// ─── Interfaces ─────────────────────────────────────────────────────────────

interface BillingRecord {
    date: string;
    account: string;
    campaignName: string;
    audienceName?: string;
    impressions?: string;
    cpm?: string;
    verismartCost: string;
    dataPartner: string;
    dataMargin?: string;
    metaCost: string;
    platformMargin?: string;
    revenue: string;
}

interface WabaBillingColumn {
    key: string;
    label: string;
}

interface CtwaCampaignWaba {
    name: string;
    number: string;
    orgId?: string;
    orgName?: string;
    clientId?: string;
    clientSubdomain?: string;
    wabaId?: string;
}

interface WabaDayWiseRecord {
    usageDate: string;
    waNumber?: string;
    utilityConsumption: number;
    utilityCost: number;
    marketingConsumption: number;
    marketingCost: number;
    authenticationConsumption: number;
    authenticationCost: number;
    totalCost: number;
    key: string;
}

interface WabaBillingRow {
    account?: string;
    month?: string;
    waba?: string;
    wabaId?: string;
    clientSubdomain?: string;
    totalCost: number;
    dayWiseRecords: WabaDayWiseRecord[];
    key: string;
}

const ALL_WABA_ORG_VALUE = 'all';
const ALL_WABA_KEY = 'all-waba';
const NULL_FILTER_VALUE = 'null';
const CTWA_CAMPAIGN_WABA_LIST_ENDPOINT = 'waba-billing/v1/waba-list';

const parseNumeric = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return value;

    const normalized = value.replace(/[^0-9.-]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—';
    return parseNumeric(value).toLocaleString();
};

const formatCurrency = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—';
    return `₹${parseNumeric(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const calculateCpm = (metaCost: number, impressions: number) => {
    if (!impressions) return null;
    return (metaCost / impressions) * 1000;
};

const getSegmentLabel = (segments: Set<string>) => {
    const segmentList = Array.from(segments);
    if (segmentList.length <= 2) return segmentList.join(', ');
    return `${segmentList.slice(0, 2).join(', ')} +${segmentList.length - 2} more`;
};

const escapeCsvValue = (value: string | number | null | undefined) =>
    `"${String(value ?? '').replace(/"/g, '""')}"`;

const BILLING_TABLE_SCROLL_X = 1760;
const WABA_TABLE_SCROLL_X = 1120;
const WABA_DAY_TABLE_SCROLL_X = 1180;
const WABA_TREND_WINDOW_SIZE = 12;

const getRecordValue = (record: AnyObject, keys: string[]) => {
    for (const key of keys) {
        if (record?.[key] !== undefined && record?.[key] !== null) return record[key];
    }
    return undefined;
};

const normalizePhoneNumber = (value: string | number | null | undefined) =>
    String(value ?? '').replace(/\D/g, '');

const phoneNumbersMatch = (left: string | number | null | undefined, right: string | number | null | undefined) => {
    const normalizedLeft = normalizePhoneNumber(left);
    const normalizedRight = normalizePhoneNumber(right);

    if (!normalizedLeft || !normalizedRight) return false;
    if (normalizedLeft === normalizedRight) return true;
    if (normalizedLeft.length < 8 || normalizedRight.length < 8) return false;

    return normalizedLeft.endsWith(normalizedRight) || normalizedRight.endsWith(normalizedLeft);
};

const formatDisplayDate = (date?: string) => {
    if (date) {
        const parsed = dayjs(date);
        return parsed.isValid() ? parsed.format('DD-MMM-YYYY') : date;
    }

    return '—';
};

const normalizeWabaDayWiseRecord = (record: AnyObject, parentKey: string, index: number): WabaDayWiseRecord => ({
    usageDate: getRecordValue(record, ['usageDate', 'usage_date']) || '',
    waNumber: getRecordValue(record, ['waNumber', 'wa_number', 'phoneNumber', 'phone_number']),
    utilityConsumption: parseNumeric(getRecordValue(record, ['utilityConsumption', 'utility_consumption'])),
    utilityCost: parseNumeric(getRecordValue(record, ['utilityCost', 'utility_cost'])),
    marketingConsumption: parseNumeric(getRecordValue(record, ['marketingConsumption', 'marketing_consumption'])),
    marketingCost: parseNumeric(getRecordValue(record, ['marketingCost', 'marketing_cost'])),
    authenticationConsumption: parseNumeric(getRecordValue(record, ['authenticationConsumption', 'authentication_consumption'])),
    authenticationCost: parseNumeric(getRecordValue(record, ['authenticationCost', 'authentication_cost'])),
    totalCost: parseNumeric(getRecordValue(record, ['totalCost', 'total_cost'])),
    key: `${parentKey}_day_${getRecordValue(record, ['usageDate', 'usage_date']) || index}_${index}`,
});

const normalizeWabaBillingRow = (record: AnyObject, index: number): WabaBillingRow => {
    const account = getRecordValue(record, ['account']);
    const month = getRecordValue(record, ['month']);
    const waba = getRecordValue(record, ['waba', 'waNumber', 'wa_number', 'phoneNumber', 'phone_number']);
    const wabaId = getRecordValue(record, ['wabaId', 'waba_id']);
    const clientSubdomain = getRecordValue(record, ['clientSubdomain', 'client_subdomain', 'subdomain']);
    const key = `${account || 'account'}_${month || 'month'}_${waba || wabaId || index}_${index}`;
    const dayWiseRecords = getRecordValue(record, ['dayWiseRecords', 'day_wise_records']) || [];

    return {
        account,
        month,
        waba,
        wabaId,
        clientSubdomain,
        totalCost: parseNumeric(getRecordValue(record, ['totalCost', 'total_cost'])),
        dayWiseRecords: Array.isArray(dayWiseRecords)
            ? dayWiseRecords.map((item: AnyObject, dayIndex: number) => normalizeWabaDayWiseRecord(item, key, dayIndex))
            : [],
        key,
    };
};

const normalizeCtwaCampaignWaba = (record: AnyObject): CtwaCampaignWaba | null => {
    const number = getRecordValue(record, [
        'number',
        'wabaNumber',
        'waba_number',
        'waNumber',
        'wa_number',
        'phoneNumber',
        'phone_number',
        'clientWaRegisteredNumber',
        'client_wa_registered_number',
    ]);

    if (!number) return null;

    const name = getRecordValue(record, [
        'name',
        'clientSubdomain',
        'client_subdomain',
        'subdomain',
        'wabaName',
        'waba_name',
    ]);

    return {
        name: String(name || number),
        number: String(number),
        orgId: getRecordValue(record, ['orgId', 'org_id']),
        orgName: getRecordValue(record, ['orgName', 'org_name']),
        clientId: getRecordValue(record, ['clientId', 'client_id']),
        clientSubdomain: getRecordValue(record, ['clientSubdomain', 'client_subdomain', 'subdomain']) || name,
        wabaId: getRecordValue(record, ['wabaId', 'waba_id']),
    };
};

const extractCtwaCampaignWabaPayload = (payload: any): CtwaCampaignWaba[] => {
    const data = payload?.data ?? payload;
    const rows = Array.isArray(data)
        ? data
        : data?.rows || data?.wabas || data?.wabaList || data?.waba_list || [];
    const wabas = Array.isArray(rows)
        ? rows
            .map((record: AnyObject) => normalizeCtwaCampaignWaba(record))
            .filter((record): record is CtwaCampaignWaba => Boolean(record?.number))
        : [];
    const uniqueWabas = new Map<string, CtwaCampaignWaba>();

    wabas.forEach((waba) => {
        const key = normalizePhoneNumber(waba.number);
        if (!key || uniqueWabas.has(key)) return;
        uniqueWabas.set(key, waba);
    });

    return Array.from(uniqueWabas.values());
};

const extractWabaBillingPayload = (payload: any) => {
    const rows = payload?.data?.rows || payload?.rows || [];
    return {
        columns: (payload?.data?.columns || []) as WabaBillingColumn[],
        dayWiseColumns: (payload?.data?.dayWiseColumns || payload?.data?.day_wise_columns || []) as WabaBillingColumn[],
        totalCost: parseNumeric(payload?.data?.totalCost ?? payload?.data?.total_cost),
        rows: Array.isArray(rows)
            ? rows.map((record: AnyObject, index: number) => normalizeWabaBillingRow(record, index))
            : [],
    };
};

const getWabaPhoneNumber = (waba: AnyObject) =>
    waba?.wabaNumber || waba?.clientWaRegisteredNumber || waba?.phoneNumber || waba?.number || waba?.ctwaNumber || '';

const getWabaClientId = (waba: AnyObject) => waba?.clientId || '';

const getWabaClientSubdomain = (waba: AnyObject) =>
    waba?.clientSubdomain || waba?.subdomain || waba?.name || waba?.ctwaName || '';

const getWabaOptionKey = (waba: AnyObject) =>
    `${getWabaClientId(waba)}::${getWabaPhoneNumber(waba)}::${waba?.wabaId || ''}`;

const findWabaMetaByNumber = (wabaOptions: AnyObject[], phoneNumber?: string) =>
    wabaOptions.find((waba) => phoneNumbersMatch(getWabaPhoneNumber(waba), phoneNumber));

const filterAndHydrateWabaRows = (
    rows: WabaBillingRow[],
    wabaOptions: AnyObject[],
    allowedNumbers: string[],
) => {
    return rows.reduce<WabaBillingRow[]>((filteredRows, row) => {
        const rowNumber = row.waba || row.dayWiseRecords.find((record) => record.waNumber)?.waNumber;
        const rowMatchesAllowedNumber = allowedNumbers.some((number) => phoneNumbersMatch(rowNumber, number));
        const dayWiseRecords = rowMatchesAllowedNumber
            ? row.dayWiseRecords
            : row.dayWiseRecords.filter((record) =>
                allowedNumbers.some((number) => phoneNumbersMatch(record.waNumber, number))
            );

        if (!rowMatchesAllowedNumber && dayWiseRecords.length === 0) return filteredRows;

        const matchedMeta = findWabaMetaByNumber(wabaOptions, rowNumber || dayWiseRecords[0]?.waNumber);

        filteredRows.push({
            ...row,
            waba: row.waba || getWabaPhoneNumber(matchedMeta as AnyObject),
            clientSubdomain: row.clientSubdomain || getWabaClientSubdomain(matchedMeta as AnyObject),
            dayWiseRecords,
            totalCost: rowMatchesAllowedNumber
                ? row.totalCost
                : dayWiseRecords.reduce((sum, record) => sum + record.totalCost, 0),
        });

        return filteredRows;
    }, []);
};

const getWabaDisplayLabel = (label: string) =>
    label
        .replace(/authentication/g, 'Authentication')
        .replace(/authentication/g, 'authentication')
        .replace(/Day Records/g, 'Billing Days');

// ─── Component ───────────────────────────────────────────────────────────────

const BillingReport: React.FC = () => {
    const { availableOrgs, currentOrg, user } = useAuthOrg();
    const { orgWabaData, fetchWabaDetails, loadingWaba } = useMetaSetup();
    const { setBreadcrumbs } = useBreadcrumbs();
    const navigate = useNavigate();

    useEffect(() => {
        setBreadcrumbs([
            { title: <div className="text-lg">Billing Report</div> },
        ]);
    }, [setBreadcrumbs, navigate]);


    const [orgId, setOrgId] = useState<string>('all');
    const [month, setMonth] = useState<Dayjs | null>(dayjs());
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<BillingRecord[]>([]);
    const [activeTab, setActiveTab] = useState('billing-report');
    const [wabaOrgId, setWabaOrgId] = useState<string>(currentOrg?.orgId || availableOrgs[0]?.orgId || ALL_WABA_ORG_VALUE);
    const [selectedWabaKey, setSelectedWabaKey] = useState<string>(ALL_WABA_KEY);
    const [wabaDateRange, setWabaDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(29, 'day'),
        dayjs(),
    ]);
    const [wabaLoading, setWabaLoading] = useState(false);
    const [wabaData, setWabaData] = useState<WabaBillingRow[]>([]);
    const [wabaColumnsMeta, setWabaColumnsMeta] = useState<WabaBillingColumn[]>([]);
    const [wabaDayWiseColumnsMeta, setWabaDayWiseColumnsMeta] = useState<WabaBillingColumn[]>([]);
    const [wabaTotalCost, setWabaTotalCost] = useState(0);
    const [wabaTrendStart, setWabaTrendStart] = useState(0);
    const [ctwaCampaignWabas, setCtwaCampaignWabas] = useState<CtwaCampaignWaba[]>([]);
    const [ctwaCampaignWabasLoading, setCtwaCampaignWabasLoading] = useState(false);
    const allAccountsLabel = normalizeRole(user?.userRole) === USER_ROLES.SALES_USER ? 'All Assigned Accounts' : 'All Accounts';

    useEffect(() => {
        if (orgWabaData.length === 0) {
            fetchWabaDetails();
        }
    }, [fetchWabaDetails, orgWabaData.length]);

    useEffect(() => {
        if (!wabaOrgId) {
            setWabaOrgId(currentOrg?.orgId || availableOrgs[0]?.orgId || ALL_WABA_ORG_VALUE);
        }
    }, [availableOrgs, currentOrg?.orgId, wabaOrgId]);

    const fetchCtwaCampaignWabaList = React.useCallback(async () => {
        setCtwaCampaignWabasLoading(true);
        setCtwaCampaignWabas([]);
        setWabaData([]);
        setWabaTotalCost(0);

        try {
            const query = new URLSearchParams();
            if (wabaOrgId !== ALL_WABA_ORG_VALUE) query.append('orgId', wabaOrgId);

            const res = await coreAPIService.get<any>(
                CTW_NODE_API_ENDPOINT || CTW_API_ENDPOINT,
                `${CTWA_CAMPAIGN_WABA_LIST_ENDPOINT}${query.toString() ? `?${query.toString()}` : ''}`
            );

            setCtwaCampaignWabas(extractCtwaCampaignWabaPayload(res));
        } catch (error) {
            console.error('Failed to fetch CTWA campaign WABA list', error);
            setCtwaCampaignWabas([]);
        } finally {
            setCtwaCampaignWabasLoading(false);
        }
    }, [wabaOrgId]);

    useEffect(() => {
        fetchCtwaCampaignWabaList();
    }, [fetchCtwaCampaignWabaList]);

    const configuredWabaOptions = React.useMemo(() => {
        if (wabaOrgId === ALL_WABA_ORG_VALUE) {
            return orgWabaData.flatMap((org) =>
                (org.wabaDetails || []).map((waba) => ({
                    ...waba,
                    orgId: org.orgId,
                    orgName: org.orgName,
                }))
            );
        }

        const orgData = orgWabaData.find((org) => org.orgId === wabaOrgId);
        return orgData?.wabaDetails || [];
    }, [orgWabaData, wabaOrgId]);

    const wabaOptions = React.useMemo(() => {
        return ctwaCampaignWabas.map((ctwaWaba) => {
            const configuredWaba = configuredWabaOptions.find((waba) =>
                phoneNumbersMatch(getWabaPhoneNumber(waba as any), ctwaWaba.number)
            );

            return {
                ...(configuredWaba || {}),
                orgId: (configuredWaba as any)?.orgId || ctwaWaba.orgId,
                orgName: (configuredWaba as any)?.orgName || ctwaWaba.orgName,
                clientId: getWabaClientId(configuredWaba as any) || ctwaWaba.clientId || '',
                subdomain: getWabaClientSubdomain(configuredWaba as any) || ctwaWaba.clientSubdomain || ctwaWaba.name,
                wabaId: (configuredWaba as any)?.wabaId || ctwaWaba.wabaId || '',
                wabaNumber: getWabaPhoneNumber(configuredWaba as any) || ctwaWaba.number,
                ctwaName: ctwaWaba.name,
                ctwaNumber: ctwaWaba.number,
                number: ctwaWaba.number,
                name: ctwaWaba.name,
            };
        });
    }, [configuredWabaOptions, ctwaCampaignWabas]);

    const ctwaCampaignWabaNumbers = React.useMemo(
        () => ctwaCampaignWabas.map((waba) => waba.number).filter(Boolean),
        [ctwaCampaignWabas]
    );

    const selectedWaba = React.useMemo(() => {
        if (selectedWabaKey === ALL_WABA_KEY) return undefined;
        return wabaOptions.find((waba) => getWabaOptionKey(waba as any) === selectedWabaKey);
    }, [selectedWabaKey, wabaOptions]);

    useEffect(() => {
        const hasSelectedWaba = selectedWabaKey === ALL_WABA_KEY ||
            wabaOptions.some((waba) => getWabaOptionKey(waba as any) === selectedWabaKey);
        if (!hasSelectedWaba) {
            setSelectedWabaKey(ALL_WABA_KEY);
        }
    }, [selectedWabaKey, wabaOptions]);

    // Calculate Totals
    let totalMetaCost = 0;
    let totalVerismartCost = 0;
    let totalRevenue = 0;
    let totalImpressions = 0;

    data.forEach((item) => {
        totalMetaCost += parseNumeric(item.metaCost);
        totalVerismartCost += parseNumeric(item.verismartCost);
        totalRevenue += parseNumeric(item.revenue);
        totalImpressions += parseNumeric(item.impressions);
    });
    const totalCpm = calculateCpm(totalMetaCost, totalImpressions);

    const isAllWabaOrg = wabaOrgId === ALL_WABA_ORG_VALUE;
    const isAllWaba = selectedWabaKey === ALL_WABA_KEY;
    const wabaClientId = isAllWaba ? null : getWabaClientId(selectedWaba as any);
    const wabaPhoneNumber = isAllWaba ? null : getWabaPhoneNumber(selectedWaba as any);

    const wabaDayRecords = React.useMemo(() => {
        return wabaData.flatMap((row) =>
            row.dayWiseRecords.map((record) => ({
                ...record,
                account: row.account,
                month: row.month,
                waba: row.waba,
                wabaId: row.wabaId,
            }))
        );
    }, [wabaData]);

    const wabaSummary = React.useMemo(() => {
        const accounts = new Set(wabaData.map((item) => item.account).filter(Boolean));
        const wabas = new Set(wabaData.map((item) => item.waba).filter(Boolean));
        const usageDates = new Set(wabaDayRecords.map((item) => item.usageDate).filter(Boolean));
        const totalUtilityConsumption = wabaDayRecords.reduce((sum, item) => sum + item.utilityConsumption, 0);
        const totalMarketingConsumption = wabaDayRecords.reduce((sum, item) => sum + item.marketingConsumption, 0);
        const totalAuthenticationConsumption = wabaDayRecords.reduce((sum, item) => sum + item.authenticationConsumption, 0);
        const totalUtilityCost = wabaDayRecords.reduce((sum, item) => sum + item.utilityCost, 0);
        const totalMarketingCost = wabaDayRecords.reduce((sum, item) => sum + item.marketingCost, 0);
        const totalAuthenticationCost = wabaDayRecords.reduce((sum, item) => sum + item.authenticationCost, 0);
        const computedTotalCost = wabaData.reduce((sum, item) => sum + item.totalCost, 0);

        return {
            totalCost: wabaTotalCost || computedTotalCost,
            accounts: accounts.size,
            wabas: wabas.size,
            usageDates: usageDates.size,
            dayRecords: wabaDayRecords.length,
            utilityConsumption: totalUtilityConsumption,
            marketingConsumption: totalMarketingConsumption,
            authenticationConsumption: totalAuthenticationConsumption,
            utilityCost: totalUtilityCost,
            marketingCost: totalMarketingCost,
            authenticationCost: totalAuthenticationCost,
            totalConsumption: totalUtilityConsumption + totalMarketingConsumption + totalAuthenticationConsumption,
        };
    }, [wabaData, wabaDayRecords, wabaTotalCost]);

    const fetchBillingReport = async () => {
        if (!month) {
            message.warning('Please select a month');
            return;
        }

        setLoading(true);
        try {
            const formattedMonth = month.format('YYYY-MM');
            const orgQParam = orgId !== 'all' ? `&orgId=${orgId}` : '';

            const res = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/billing/monthly-report?billingMonth=${formattedMonth}${orgQParam}`
            );

            if (res?.meta?.code === 200 || res?.data) {
                setData(res.data || []);
            } else {
                setData([]);
            }
        } catch (err: any) {
            console.error('Failed to fetch billing report', err);
            // Fallback/Mock Data if endpoint doesn't exist yet, so UI still shows structural flow
            if (err?.response?.status === 404) {
                message.info('Billing endpoint not fully implemented yet. Please ensure backend is deployed.');
                setData([]);
            } else {
                message.error('Failed to load billing report');
                setData([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchWabaBilling = async () => {
        if (!wabaDateRange?.[0] || !wabaDateRange?.[1]) {
            message.warning('Please select a date range');
            return;
        }

        if (ctwaCampaignWabasLoading) {
            message.info('Loading CTWA campaign WABA numbers');
            return;
        }

        const allowedWabaNumbers = isAllWaba
            ? ctwaCampaignWabaNumbers
            : [wabaPhoneNumber].filter((phoneNumber): phoneNumber is string => Boolean(phoneNumber));

        if (allowedWabaNumbers.length === 0) {
            message.warning('No CTWA campaign WABA numbers found');
            return;
        }

        if (!isAllWaba && !wabaPhoneNumber) {
            message.warning('Please select a WABA number');
            return;
        }

        setWabaLoading(true);
        try {
            const query = new URLSearchParams({
                startDate: String(wabaDateRange[0].startOf('day').format("YYYY-MM-DD")),
                endDate: String(wabaDateRange[1].endOf('day').format("YYYY-MM-DD")),
            });

            if (!isAllWabaOrg) query.append("orgId", wabaOrgId);
            if (!isAllWaba && wabaClientId) query.append("clientId", wabaClientId);

            const res = await coreAPIService.get<any>(
                CTW_NODE_API_ENDPOINT || CTW_API_ENDPOINT,
                `waba-billing/v1/analytics?${query.toString()}`
            );

            const payload = extractWabaBillingPayload(res);
            const filteredRows = filterAndHydrateWabaRows(payload.rows, wabaOptions as AnyObject[], allowedWabaNumbers);
            setWabaColumnsMeta(payload.columns);
            setWabaDayWiseColumnsMeta(payload.dayWiseColumns);
            setWabaTotalCost(filteredRows.reduce((sum, row) => sum + row.totalCost, 0));
            setWabaData(filteredRows);
        } catch (err) {
            console.error('Failed to fetch WABA billing report', err);
            message.error('Failed to load WABA billing report');
            setWabaColumnsMeta([]);
            setWabaDayWiseColumnsMeta([]);
            setWabaTotalCost(0);
            setWabaData([]);
        } finally {
            setWabaLoading(false);
        }
    };

    useEffect(() => {
        fetchBillingReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tableData = React.useMemo(() => {
        const grouped: Record<string, any> = {};

        data.forEach(item => {
            const acc = item.account || 'Unknown Account';
            const camp = item.campaignName || 'Unknown Campaign';
            const segment = item.audienceName || '';
            const metaCost = parseNumeric(item.metaCost);
            const verismartCost = parseNumeric(item.verismartCost);
            const dataPartner = item.dataPartner;
            const revenue = parseNumeric(item.revenue);
            const impressions = parseNumeric(item.impressions);
            const cpm = parseNumeric(item.cpm);

            if (!grouped[acc]) {
                grouped[acc] = {
                    key: `account_${acc}`,
                    account: acc,
                    date: '',
                    campaignName: '',
                    audienceName: '',
                    impressions: 0,
                    cpm: null,
                    metaCost: 0,
                    verismartCost: 0,
                    dataPartner: '',
                    revenue: 0,
                    _campaigns: {}
                };
            }

            grouped[acc].metaCost += metaCost;
            grouped[acc].verismartCost += verismartCost;
            grouped[acc].revenue += revenue;
            grouped[acc].impressions += impressions;

            const accGroup = grouped[acc];

            if (!accGroup._campaigns[camp]) {
                accGroup._campaigns[camp] = {
                    key: `acc_${acc}_camp_${camp}`,
                    account: acc,
                    date: '',
                    campaignName: camp,
                    audienceName: '',
                    impressions: 0,
                    cpm: null,
                    metaCost: 0,
                    verismartCost: 0,
                    dataPartner: dataPartner,
                    revenue: 0,
                    dataMargin: null,
                    platformMargin: null,
                    _segments: new Set<string>(),
                    children: []
                };
            }

            if (segment) {
                accGroup._campaigns[camp]._segments.add(segment);
            }

            accGroup._campaigns[camp].metaCost += metaCost;
            accGroup._campaigns[camp].verismartCost += verismartCost;
            accGroup._campaigns[camp].revenue += revenue;
            accGroup._campaigns[camp].impressions += impressions;

            accGroup._campaigns[camp].children.push({
                ...item,
                account: acc, // hide to avoid redundancy in the nested view
                campaignName: camp,
                audienceName: segment,
                key: `${item.date}_${item.account}_${item.campaignName}_${item.audienceName}_${Math.random()}`,
                impressions,
                cpm,
                metaCost,
                dataPartner: dataPartner,
                verismartCost,
                revenue,
            });
        });

        // Convert _campaigns map to children arrays and sort dates
        const result = Object.values(grouped).map(group => {
            const campaigns = Object.values(group._campaigns).map((campGroup: any) => {
                campGroup.children.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
                campGroup.audienceName = getSegmentLabel(campGroup._segments);
                // campGroup.cpm = calculateCpm(campGroup.metaCost, campGroup.impressions);
                delete campGroup._segments;
                return campGroup;
            });

            delete group._campaigns;
            // group.cpm = calculateCpm(group.metaCost, group.impressions);
            group.children = campaigns;

            return group;
        });

        return result;
    }, [data]);

    const wabaTableData = React.useMemo(() => {
        return [...wabaData].sort((a, b) => {
            const accountCompare = String(a.account || '').localeCompare(String(b.account || ''));
            if (accountCompare !== 0) return accountCompare;
            const monthCompare = String(b.month || '').localeCompare(String(a.month || ''));
            if (monthCompare !== 0) return monthCompare;
            return String(a.waba || '').localeCompare(String(b.waba || ''));
        });
    }, [wabaData]);

    const wabaDailyTrend = React.useMemo(() => {
        const grouped: Record<string, {
            usageDate: string;
            utilityConsumption: number;
            marketingConsumption: number;
            authenticationConsumption: number;
            utilityCost: number;
            marketingCost: number;
            authenticationCost: number;
            totalCost: number;
            accounts: Set<string>;
            wabas: Set<string>;
            records: number;
        }> = {};

        wabaDayRecords.forEach((item) => {
            const usageDate = item.usageDate || 'Unknown Date';
            if (!grouped[usageDate]) {
                grouped[usageDate] = {
                    usageDate,
                    utilityConsumption: 0,
                    marketingConsumption: 0,
                    authenticationConsumption: 0,
                    utilityCost: 0,
                    marketingCost: 0,
                    authenticationCost: 0,
                    totalCost: 0,
                    accounts: new Set<string>(),
                    wabas: new Set<string>(),
                    records: 0,
                };
            }

            grouped[usageDate].utilityConsumption += item.utilityConsumption;
            grouped[usageDate].marketingConsumption += item.marketingConsumption;
            grouped[usageDate].authenticationConsumption += item.authenticationConsumption;
            grouped[usageDate].utilityCost += item.utilityCost;
            grouped[usageDate].marketingCost += item.marketingCost;
            grouped[usageDate].authenticationCost += item.authenticationCost;
            grouped[usageDate].totalCost += item.totalCost;
            if (item.account) grouped[usageDate].accounts.add(String(item.account));
            if (item.waba) grouped[usageDate].wabas.add(String(item.waba));
            grouped[usageDate].records += 1;
        });

        return Object.values(grouped)
            .map((item) => ({
                ...item,
                totalConsumption: item.utilityConsumption + item.marketingConsumption + item.authenticationConsumption,
                accountCount: item.accounts.size,
                wabaCount: item.wabas.size,
            }))
            .sort((a, b) => new Date(a.usageDate).getTime() - new Date(b.usageDate).getTime());
    }, [wabaDayRecords]);

    useEffect(() => {
        setWabaTrendStart(Math.max(wabaDailyTrend.length - WABA_TREND_WINDOW_SIZE, 0));
    }, [wabaDailyTrend.length]);

    const maxWabaTrendStart = Math.max(wabaDailyTrend.length - WABA_TREND_WINDOW_SIZE, 0);
    const visibleWabaDailyTrend = wabaDailyTrend.slice(wabaTrendStart, wabaTrendStart + WABA_TREND_WINDOW_SIZE);
    const maxDailyWabaConsumption = Math.max(...wabaDailyTrend.map((item) => item.totalConsumption), 0);

    const handleExport = () => {
        if (data.length === 0) {
            message.warning('No data to export');
            return;
        }

        // Basic CSV Export
        const headers = [
            'Account',
            'Date',
            'Campaign Name',
            'Segment Name',
            'Impressions',
            'CPM',
            'Platform Margin %',
            'Meta Cost',
            'Data Portion %',
            'Data Partner',
            'Data Cost',
            'Revenue (MTD)',
        ];
        const csvContent = [
            headers.join(','),
            ...data.map((r) => {
                const rowMetaCost = parseNumeric(r.metaCost);
                const rowImpressions = parseNumeric(r.impressions);
                const rowCpm = parseNumeric(r.cpm);
                const rowPlatformMargin = parseNumeric(r.platformMargin);
                const rowDataMargin = parseNumeric(r.dataMargin);

                return [
                    escapeCsvValue(r.account),
                    escapeCsvValue(r.date ? dayjs(r.date).format('DD-MMM-YYYY') : ''),
                    escapeCsvValue(r.campaignName),
                    escapeCsvValue(r.audienceName),
                    rowImpressions,
                    rowCpm || 0,
                    rowPlatformMargin || 0,
                    rowMetaCost,
                    rowDataMargin || 0,
                    escapeCsvValue(r.dataPartner),
                    parseNumeric(r.verismartCost),
                    parseNumeric(r.revenue),
                ].join(',');
            })
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Billing_Report_${month?.format('MMM_YYYY') || 'Export'}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleWabaExport = () => {
        if (wabaData.length === 0) {
            message.warning('No WABA billing data to export');
            return;
        }

        const headers = [
            'Account',
            'Month',
            'WABA',
            'Client Subdomain',
            'Usage Date',
            // 'Country',
            'WA Number',
            'Utility Count',
            'Utility Cost',
            'Marketing Count',
            'Marketing Cost',
            'Authentication Count',
            'Authentication Cost',
            'Total Cost',
        ];

        const rows = wabaData.flatMap((record) => {
            if (record.dayWiseRecords.length === 0) {
                return [[
                    escapeCsvValue(record.account),
                    escapeCsvValue(record.month),
                    escapeCsvValue(record.waba),
                    escapeCsvValue(record.clientSubdomain),
                    escapeCsvValue(''),
                    escapeCsvValue(''),
                    escapeCsvValue(''),
                    0,
                    0,
                    0,
                    0,
                    0,
                    0,
                    record.totalCost,
                ].join(',')];
            }

            return record.dayWiseRecords.map((dayRecord) => [
                escapeCsvValue(record.account),
                escapeCsvValue(record.month),
                escapeCsvValue(record.waba),
                escapeCsvValue(record.clientSubdomain),
                escapeCsvValue(dayRecord.usageDate),
                escapeCsvValue(dayRecord.waNumber),
                dayRecord.utilityConsumption,
                dayRecord.utilityCost,
                dayRecord.marketingConsumption,
                dayRecord.marketingCost,
                dayRecord.authenticationConsumption,
                dayRecord.authenticationCost,
                dayRecord.totalCost,
            ].join(','));
        });

        const csvContent = [
            headers.join(','),
            ...rows,
            [
                escapeCsvValue('Totals'),
                escapeCsvValue(''),
                escapeCsvValue(''),
                escapeCsvValue(''),
                escapeCsvValue(''),
                escapeCsvValue(''),
                escapeCsvValue(''),
                wabaSummary.utilityConsumption,
                wabaSummary.utilityCost,
                wabaSummary.marketingConsumption,
                wabaSummary.marketingCost,
                wabaSummary.authenticationConsumption,
                wabaSummary.authenticationCost,
                wabaSummary.totalCost,
            ].join(','),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `WABA_Billing_${wabaDateRange[0].format('YYYY_MM_DD')}_${wabaDateRange[1].format('YYYY_MM_DD')}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            title: 'Account',
            dataIndex: 'account',
            key: 'account',
            width: 220,
            ellipsis: true,
            render: (text: string) => <Text strong>{text || '—'}</Text>,
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 140,
            render: (text: string) => text ? dayjs(text).format('DD-MMM-YYYY') : '—',
        },
        {
            title: 'Campaign Name',
            dataIndex: 'campaignName',
            key: 'campaignName',
            width: 260,
            ellipsis: true,
            render: (text: string) => text || '—',
        },
        {
            title: 'Segment Name',
            dataIndex: 'audienceName',
            key: 'audienceName',
            width: 240,
            ellipsis: true,
            render: (text: string) => text || '—',
        },
        {
            title: 'Impressions',
            dataIndex: 'impressions',
            key: 'impressions',
            align: 'right' as const,
            width: 150,
            render: (val: any) => formatNumber(val),
        },
        {
            title: 'CPM',
            dataIndex: 'cpm',
            key: 'cpm',
            align: 'right' as const,
            width: 130,
            render: (val: any) => formatCurrency(val),
        },
        {
            title: 'Platform Margin %',
            dataIndex: 'platformMargin',
            key: 'platformMargin',
            align: 'right' as const,
            width: 170,
            render: (val: any) => formatNumber(val),
        },
        {
            title: 'Meta Cost',
            dataIndex: 'metaCost',
            key: 'metaCost',
            align: 'right' as const,
            width: 140,
            render: (val: any) => formatCurrency(val),
        },
        {
            title: 'Data Portion %',
            dataIndex: 'dataMargin',
            key: 'dataMargin',
            align: 'right' as const,
            width: 160,
            render: (val: any) => formatNumber(val),
        },
        {
            title: 'Data Partner',
            dataIndex: 'dataPartner',
            key: 'dataPartner',
            align: 'right' as const,
            width: 150,
            render: (text: string) => text || '—',
        },
        {
            title: 'Data Cost',
            dataIndex: 'verismartCost',
            key: 'verismartCost',
            align: 'right' as const,
            width: 150,
            render: (val: any) => formatCurrency(val),
        },
        {
            title: 'Revenue (MTD)',
            dataIndex: 'revenue',
            key: 'revenue',
            align: 'right' as const,
            width: 200,
            render: (val: any) => (
                <Text type="success" strong>
                    {formatCurrency(val)}
                </Text>
            ),
        },
    ];

    const getWabaColumnLabel = (columnsList: WabaBillingColumn[], key: string, fallback: string) =>
        getWabaDisplayLabel(columnsList.find((column) => column.key === key)?.label || fallback);

    const wabaColumns = [
        {
            title: getWabaColumnLabel(wabaColumnsMeta, 'account', 'Account'),
            dataIndex: 'account',
            key: 'account',
            width: 220,
            ellipsis: true,
            render: (text: string) => <Text strong>{text || '—'}</Text>,
        },
        {
            title: getWabaColumnLabel(wabaColumnsMeta, 'month', 'Month'),
            dataIndex: 'month',
            key: 'month',
            width: 120,
            render: (text: string) => text || '—',
        },
        {
            title: getWabaColumnLabel(wabaColumnsMeta, 'waba', 'WABA'),
            dataIndex: 'waba',
            key: 'waba',
            width: 230,
            ellipsis: true,
            render: (text: string, record: WabaBillingRow) => (
                <div className="flex flex-col">
                    <Text strong>{text || '—'}</Text>
                    {record.clientSubdomain && <Text type="secondary" className="text-xs">{record.clientSubdomain}</Text>}
                </div>
            ),
        },
        {
            title: 'Billing Days',
            dataIndex: 'dayWiseRecords',
            key: 'dayWiseRecords',
            align: 'right' as const,
            width: 140,
            render: (records: WabaDayWiseRecord[]) => formatNumber(records?.length || 0),
        },
        {
            title: getWabaColumnLabel(wabaColumnsMeta, 'total_cost', 'Total Cost'),
            dataIndex: 'totalCost',
            key: 'totalCost',
            align: 'right' as const,
            width: 150,
            render: (value: number) => <Text strong>{formatCurrency(value)}</Text>,
        },
    ];

    const wabaDayWiseColumns = [
        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'usage_date', 'Usage Date'),
            dataIndex: 'usageDate',
            key: 'usageDate',
            width: 140,
            render: (text: string) => formatDisplayDate(text),
        },
        // {
        //     title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'country', 'Country'),
        //     dataIndex: 'country',
        //     key: 'country',
        //     width: 100,
        //     render: (text: string) => text || '—',
        // },
        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'wa_number', 'WA Number'),
            dataIndex: 'waNumber',
            key: 'waNumber',
            width: 160,
            render: (text: string) => text || '—',
        },
        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'utility_consumption', 'Utility Count'),
            dataIndex: 'utilityConsumption',
            key: 'utilityConsumption',
            align: 'right' as const,
            width: 140,
            render: (value: number) => formatNumber(value),
        },
        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'utility_cost', 'Utility Cost'),
            dataIndex: 'utilityCost',
            key: 'utilityCost',
            align: 'right' as const,
            width: 140,
            render: (value: number) => formatCurrency(value),
        },
        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'marketing_consumption', 'Marketing Count'),
            dataIndex: 'marketingConsumption',
            key: 'marketingConsumption',
            align: 'right' as const,
            width: 160,
            render: (value: number) => formatNumber(value),
        },
        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'marketing_cost', 'Marketing Cost'),
            dataIndex: 'marketingCost',
            key: 'marketingCost',
            align: 'right' as const,
            width: 150,
            render: (value: number) => formatCurrency(value),
        },
        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'authentication_consumption', 'Authentication Count'),
            dataIndex: 'authenticationConsumption',
            key: 'authenticationConsumption',
            align: 'right' as const,
            width: 160,
            render: (value: number) => formatNumber(value),
        },
        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'authentication_cost', 'Authentication Cost'),
            dataIndex: 'authenticationCost',
            key: 'authenticationCost',
            align: 'right' as const,
            width: 150,
            render: (value: number) => formatCurrency(value),
        },

        {
            title: getWabaColumnLabel(wabaDayWiseColumnsMeta, 'total_cost', 'Total Cost'),
            dataIndex: 'totalCost',
            key: 'totalCost',
            align: 'right' as const,
            width: 140,
            render: (value: number) => <Text strong>{formatCurrency(value)}</Text>,
        },
    ];

    const wabaSummaryTiles = [
        { label: 'Total Cost', value: formatCurrency(wabaSummary.totalCost), subtitle: `${[...new Set(wabaData.map((wb) => wb.account))].length.toLocaleString()} account/ ${[...new Set(wabaData.map((wb) => wb.waba))].length.toLocaleString()} WABA row(s)` },
        { label: 'Utility Count', value: formatNumber(wabaSummary.utilityConsumption) },
        { label: 'Marketing Count', value: formatNumber(wabaSummary.marketingConsumption) },
        { label: 'Authentication Count', value: formatNumber(wabaSummary.authenticationConsumption) },
        // { label: 'Scope', value: `${wabaSummary.accounts}/${wabaSummary.countries}`, subtitle: 'Accounts / countries' },
    ];

    const monthlyBillingTab = (
        <div>
            <div className="flex justify-between items-center mb-6">
                <Heading
                    title="Monthly Billing Report"
                    subtitle="View month-to-date revenue, Meta cost, and Data cost per campaign"
                />
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExport}
                    disabled={data.length === 0}
                    className="bg-[#1F344C]"
                >
                    Export Data
                </Button>
            </div>

            <Card className="mb-6 shadow-sm border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                    <div>
                        <Text type="secondary" className="mb-1">
                            Account (Organization)
                        </Text>
                        <Select
                            className="w-full"
                            placeholder="Select Account"
                            value={orgId}
                            onChange={(val) => setOrgId(val)}
                            showSearch
                            filterOption={(input, option) =>
                                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            <Option value="all">{allAccountsLabel}</Option>
                            {availableOrgs.map((org) => (
                                <Option key={org.orgId} value={org.orgId}>
                                    <Tooltip title={org.orgName}>{org.orgName}</Tooltip>
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Text type="secondary" className="mb-1">
                            Billing Month
                        </Text>
                        <DatePicker
                            picker="month"
                            className="w-full"
                            value={month}
                            onChange={(val) => setMonth(val)}
                            allowClear={false}
                        />
                    </div>

                    <div className="md:col-span-2 flex justify-end gap-3">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchBillingReport}
                        >
                            Reset / Refresh
                        </Button>
                        <Button
                            type="primary"
                            onClick={fetchBillingReport}
                            loading={loading}
                            className="bg-[#1F344C]"
                        >
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </Card>

            <Card className="shadow-sm border border-gray-100" bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={tableData}
                    rowKey="key"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: BILLING_TABLE_SCROLL_X }}
                    tableLayout="fixed"
                    summary={() => {
                        if (data.length === 0) return null;
                        return (
                            <Table.Summary>
                                <Table.Summary.Row className="font-semibold">
                                    <Table.Summary.Cell index={0} colSpan={4} align="left">
                                        Grand Total
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">
                                        <Text strong>{totalImpressions.toLocaleString()}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">
                                        <Text strong>{formatCurrency(totalCpm)}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} />
                                    <Table.Summary.Cell index={7} align="right">
                                        <Text strong>{formatCurrency(totalMetaCost)}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={8} />
                                    <Table.Summary.Cell index={9} />
                                    <Table.Summary.Cell index={10} align="right">
                                        <Text strong>{formatCurrency(totalVerismartCost)}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={11} align="right">
                                        <Text type="success" strong>
                                            {formatCurrency(totalRevenue)}
                                        </Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
            </Card>
        </div>
    );

    const wabaBillingTab = (
        <div>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
                <Heading
                    title="WABA Billing"
                    subtitle="WhatsApp consumption and cost for numbers used in CTWA campaigns"
                />
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleWabaExport}
                    disabled={wabaData.length === 0}
                    className="bg-[#1F344C] self-start lg:self-auto"
                >
                    Export Data
                </Button>
            </div>

            <Card className="mb-6 shadow-sm border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                    <div>
                        <Text type="secondary" className="mb-1">
                            Account (Organization)
                        </Text>
                        <Select
                            className="w-full"
                            placeholder="Select Account"
                            value={wabaOrgId || undefined}
                            onChange={(val) => {
                                setWabaOrgId(val);
                                setSelectedWabaKey(ALL_WABA_KEY);
                                setWabaData([]);
                                setWabaTotalCost(0);
                            }}
                            showSearch
                            filterOption={(input, option) =>
                                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            <Option value={ALL_WABA_ORG_VALUE}>{allAccountsLabel}</Option>
                            {availableOrgs.map((org) => (
                                <Option key={org.orgId} value={org.orgId}>
                                    <Tooltip title={org.orgName}>{org.orgName}</Tooltip>
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Text type="secondary" className="mb-1">
                            WABA Number
                        </Text>
                        <Select
                            className="w-full"
                            placeholder="Select WABA"
                            value={selectedWabaKey}
                            onChange={(val) => {
                                setSelectedWabaKey(val);
                                setWabaData([]);
                                setWabaTotalCost(0);
                            }}
                            loading={loadingWaba || ctwaCampaignWabasLoading}
                            disabled={loadingWaba || ctwaCampaignWabasLoading}
                            notFoundContent={ctwaCampaignWabasLoading ? 'Loading CTWA WABAs...' : 'No CTWA campaign WABAs found'}
                            showSearch
                            filterOption={(input, option) =>
                                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            <Option value={ALL_WABA_KEY} label="All CTWA WABAs">
                                All CTWA WABAs
                            </Option>
                            {wabaOptions.map((waba) => {
                                const phoneNumber = getWabaPhoneNumber(waba as any);
                                const clientId = getWabaClientId(waba as any);
                                const orgName = (waba as any).orgName;
                                const subdomain = (waba as any).subdomain;
                                const optionLabel = `${phoneNumber} ${clientId} ${orgName || ''} ${subdomain || ''}`;

                                return (
                                    <Option
                                        key={getWabaOptionKey(waba as any)}
                                        value={getWabaOptionKey(waba as any)}
                                        label={optionLabel}
                                    >
                                        <div className="flex flex-col">
                                            <Tooltip title={<>{phoneNumber || 'Unknown number'} {subdomain ? `${subdomain}` : ''}{orgName ? ` - ${orgName}` : ''}</>}>

                                                <span>{phoneNumber || 'Unknown number'} <Text type="secondary" className="text-xs">{subdomain ? `${subdomain}` : ''}{orgName ? ` - ${orgName}` : ''}</Text></span>
                                            </Tooltip>
                                            {/* <Text type="secondary" className="text-xs">
                                                {subdomain ? `${subdomain}` : ''}{orgName ? ` - ${orgName}` : ''}
                                            </Text> */}
                                        </div>
                                    </Option>
                                );
                            })}
                        </Select>
                    </div>

                    <div>
                        <Text type="secondary" className="mb-1">
                            Date Range
                        </Text>
                        <RangePicker
                            className="w-full"
                            value={wabaDateRange}
                            allowClear={false}
                            onChange={(dates) => {
                                if (dates?.[0] && dates?.[1]) {
                                    setWabaDateRange([dates[0], dates[1]]);
                                }
                            }}
                        />
                    </div>

                    <div className="flex gap-3 ml-2">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchWabaBilling}
                        >
                            Refresh
                        </Button>
                        <Button
                            type="primary"
                            onClick={fetchWabaBilling}
                            loading={wabaLoading || ctwaCampaignWabasLoading}
                            className="bg-[#1F344C]"
                        >
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                {wabaSummaryTiles.map((tile) => (
                    <div key={tile.label} className="bg-white border border-gray-100 rounded-lg shadow-sm p-4">
                        <Text type="secondary" className="text-xs uppercase tracking-wide">{tile.label}</Text>
                        <div className="text-2xl font-semibold text-gray-900 mt-1">{tile.value}</div>
                        <Text type="secondary" className="text-xs">{tile.subtitle}</Text>
                    </div>
                ))}
            </div>

            {/* <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
                <Card
                    className="xl:col-span-3 shadow-sm border border-gray-100"
                    title="Daily Consumption"
                    extra={wabaDailyTrend.length > 0 ? (
                        <Text type="secondary">
                            {formatDisplayDate(visibleWabaDailyTrend[0]?.usageDate)} - {formatDisplayDate(visibleWabaDailyTrend[visibleWabaDailyTrend.length - 1]?.usageDate)}
                        </Text>
                    ) : null}
                >
                    {wabaDailyTrend.length > 0 ? (
                        <div className="space-y-4 h-[28rem] overflow-auto">
                            <div className="space-y-3">
                                {visibleWabaDailyTrend.map((item) => {
                                    const utilityWidth = item.totalConsumption
                                        ? (item.utilityConsumption / item.totalConsumption) * 100
                                        : 0;
                                    const marketingWidth = item.totalConsumption
                                        ? (item.marketingConsumption / item.totalConsumption) * 100
                                        : 0;
                                    const authenticationWidth = item.totalConsumption
                                        ? (item.authenticationConsumption / item.totalConsumption) * 100
                                        : 0;
                                    const totalWidth = maxDailyWabaConsumption
                                        ? Math.max((item.totalConsumption / maxDailyWabaConsumption) * 100, 2)
                                        : 0;

                                    return (
                                        <div key={item.usageDate} className="grid grid-cols-[110px_1fr_150px] gap-3 items-center">
                                            <Text className="text-sm">{formatDisplayDate(item.usageDate)}</Text>
                                            <div>
                                                <div className="h-3 bg-gray-100 rounded-full overflow-hidden" style={{ width: `${totalWidth}%` }}>
                                                    <div className="h-full bg-[#1F344C] inline-block" style={{ width: `${utilityWidth}%` }} />
                                                    <div className="h-full bg-[#4B8F8C] inline-block" style={{ width: `${marketingWidth}%` }} />
                                                    <div className="h-full bg-[#111F8C] inline-block" style={{ width: `${authenticationWidth}%` }} />
                                                </div>
                                                <Text type="secondary" className="text-xs">
                                                    {item.accountCount} account(s), {item.wabaCount} WABA(s), {item.records} record(s)
                                                </Text>
                                            </div>
                                            <div className="text-right">
                                                <Text strong>{formatNumber(item.totalConsumption)}</Text>
                                                <Text type="secondary" className="block text-xs">{formatCurrency(item.totalCost)}</Text>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {maxWabaTrendStart > 0 && (
                                <div className="px-1">
                                    <Slider
                                        min={0}
                                        max={maxWabaTrendStart}
                                        value={Math.min(wabaTrendStart, maxWabaTrendStart)}
                                        onChange={(value) => setWabaTrendStart(Array.isArray(value) ? value[0] : value)}
                                        tooltip={{ formatter: (value) => `Start day ${Number(value || 0) + 1}` }}
                                    />
                                    <div className="flex justify-between">
                                        <Text type="secondary" className="text-xs">{formatDisplayDate(wabaDailyTrend[0]?.usageDate)}</Text>
                                        <Text type="secondary" className="text-xs">{formatDisplayDate(wabaDailyTrend[wabaDailyTrend.length - 1]?.usageDate)}</Text>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="inline-block h-2 w-5 rounded-sm bg-[#1F344C]" />
                                    <Text type="secondary" className="text-xs">Utility</Text>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="inline-block h-2 w-5 rounded-sm bg-[#4B8F8C]" />
                                    <Text type="secondary" className="text-xs">Marketing</Text>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="inline-block h-2 w-5 rounded-sm bg-[#111F8C]" />
                                    <Text type="secondary" className="text-xs">Authentication</Text>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Text type="secondary">No WABA billing data loaded</Text>
                    )}
                </Card>

                <Card className="shadow-sm border border-gray-100" title="Selected WABA">
                    <div className="space-y-3">
                        <div>
                            <Text type="secondary" className="block text-xs">Organization</Text>
                            <Text strong>{isAllWabaOrg ? allAccountsLabel : availableOrgs.find((org) => org.orgId === wabaOrgId)?.orgName || '—'}</Text>
                        </div>
                        <div>
                            <Text type="secondary" className="block text-xs">Client ID</Text>
                            <Text>{isAllWaba ? 'All clients' : wabaClientId || '—'}</Text>
                        </div>
                        <div>
                            <Text type="secondary" className="block text-xs">Phone Number</Text>
                            <Text>{isAllWaba ? 'All WABAs' : wabaPhoneNumber || '—'}</Text>
                        </div>
                        <div>
                            <Text type="secondary" className="block text-xs">Date Range</Text>
                            <Text>{`${wabaDateRange[0].format('DD-MMM-YYYY')} to ${wabaDateRange[1].format('DD-MMM-YYYY')}`}</Text>
                        </div>
                    </div>
                </Card>
            </div> */}

            <Card className="shadow-sm border border-gray-100" bodyStyle={{ padding: 0 }}>
                <Table
                    columns={wabaColumns}
                    dataSource={wabaTableData}
                    rowKey="key"
                    loading={wabaLoading}
                    expandable={{
                        expandedRowRender: (record: WabaBillingRow) => (
                            <Table
                                columns={wabaDayWiseColumns}
                                dataSource={record.dayWiseRecords}
                                rowKey="key"
                                // pagination={{
                                //     pageSize: 8,
                                //     showSizeChanger: true,
                                //     pageSizeOptions: ['8', '16', '32'],
                                // }}
                                pagination={false}
                                scroll={{ x: WABA_DAY_TABLE_SCROLL_X }}
                                tableLayout="fixed"
                                size="small"
                                className='p-2'
                                summary={() => {
                                    if (record.dayWiseRecords.length === 0) return null;
                                    const utilityConsumption = record.dayWiseRecords.reduce((sum, item) => sum + item.utilityConsumption, 0);
                                    const marketingConsumption = record.dayWiseRecords.reduce((sum, item) => sum + item.marketingConsumption, 0);
                                    const authenticationConsumption = record.dayWiseRecords.reduce((sum, item) => sum + item.authenticationConsumption, 0);
                                    const utilityCost = record.dayWiseRecords.reduce((sum, item) => sum + item.utilityCost, 0);
                                    const marketingCost = record.dayWiseRecords.reduce((sum, item) => sum + item.marketingCost, 0);
                                    const authenticationCost = record.dayWiseRecords.reduce((sum, item) => sum + item.authenticationCost, 0);
                                    const totalCost = record.dayWiseRecords.reduce((sum, item) => sum + item.totalCost, 0);

                                    return (
                                        <Table.Summary>
                                            <Table.Summary.Row className="font-semibold">
                                                <Table.Summary.Cell index={0} colSpan={2}>
                                                    Day Total
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={3} align="right">
                                                    <Text strong>{formatNumber(utilityConsumption)}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={4} align="right">
                                                    <Text strong>{formatCurrency(utilityCost)}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={5} align="right">
                                                    <Text strong>{formatNumber(marketingConsumption)}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={6} align="right">
                                                    <Text strong>{formatCurrency(marketingCost)}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={7} align="right">
                                                    <Text strong>{formatNumber(authenticationConsumption)}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={8} align="right">
                                                    <Text strong>{formatCurrency(authenticationCost)}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={9} align="right">
                                                    <Text strong>{formatCurrency(totalCost)}</Text>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        ),
                        rowExpandable: (record) => record.dayWiseRecords.length > 0,
                    }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        showTotal: (total) => `${total} account/WABA row(s)`,
                    }}
                    scroll={{ x: WABA_TABLE_SCROLL_X, y: 560 }}
                    tableLayout="fixed"
                    summary={() => {
                        if (wabaData.length === 0) return null;
                        return (
                            <Table.Summary>
                                <Table.Summary.Row className="font-semibold">
                                    <Table.Summary.Cell index={0} colSpan={5}>
                                        Grand Total
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">
                                        <Text strong>{formatCurrency(wabaSummary.totalCost)}</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
            </Card>
        </div>
    );

    return (
        <CommonLayout>
            <div className="p-6">
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    items={[
                        {
                            key: 'billing-report',
                            label: 'Billing Report',
                            children: monthlyBillingTab,
                        },
                        {
                            key: 'waba-billing',
                            label: 'WABA Billing',
                            children: wabaBillingTab,
                        },
                    ]}
                />
            </div>
        </CommonLayout>
    );
};

export default BillingReport;
